import React, { useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, KV, Modal, PageHead, Textarea, dateTimeStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { cancellationApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: 'pending', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

/**
 * Buyer cancellation requests.
 *
 * Approving is what actually cancels the order: it releases the reserved stock
 * and tells the seller to stop packing, so it is a decision rather than a
 * formality.
 */
const Cancellations = () => {
  const toast = useToast();
  const list = useList(cancellationApi.list, { status: 'pending' });

  const [review, setReview] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async decision => {
    setBusy(true);
    try {
      const res = await cancellationApi.review(review._id, { decision, note });
      toast.success(res.message);
      setReview(null);
      setNote('');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'order',
      label: 'Order',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.orderNumber}</div>
          <div className="text-sm text-muted">{inr(row.grandTotal)} · {num(row.items?.length || 0)} item(s)</div>
        </div>
      ),
    },
    { key: 'buyer', label: 'Buyer', render: row => <IdCell name={row.buyer?.name} sub={row.buyer?.code} /> },
    { key: 'seller', label: 'Seller', render: row => <IdCell name={row.seller?.name} sub={row.seller?.code} /> },
    {
      key: 'reason',
      label: 'Reason',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.cancellation?.reason || '—'}</div>
          {row.cancellation?.note && <div className="text-sm text-muted">{row.cancellation.note}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Order status',
      render: row => <Badge status={row.status}>{row.status}</Badge>,
    },
    {
      key: 'requestedAt',
      label: 'Requested',
      render: row => <span className="text-sm text-muted">{dateTimeStr(row.cancellation?.requestedAt)}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row =>
        row.cancellation?.status === 'pending' ? (
          <Button size="sm" onClick={() => { setReview(row); setNote(''); }}>Review</Button>
        ) : (
          <Badge status={row.cancellation?.status}>{row.cancellation?.status}</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHead
        title="Cancellation requests"
        subtitle="Buyers can only cancel before shipment, and only with your approval."
      />

      <div className="filters">
        {TABS.map(tab => (
          <Button
            key={tab.value}
            size="sm"
            variant={list.filters.status === tab.value ? 'primary' : 'ghost'}
            onClick={() => list.setFilter('status', tab.value)}
          >
            {tab.label}
          </Button>
        ))}
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} requests` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!review}
        title={`Cancellation — ${review?.orderNumber || ''}`}
        subtitle={review?.cancellation?.reason}
        onClose={() => setReview(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReview(null)}>Close</Button>
            <Button variant="danger" loading={busy} onClick={() => decide('rejected')}>Reject</Button>
            <Button loading={busy} onClick={() => decide('approved')}>Approve & cancel</Button>
          </>
        }
      >
        {review && (
          <>
            <KV
              items={[
                { label: 'Buyer', value: review.buyer?.name },
                { label: 'Seller', value: review.seller?.name },
                { label: 'Order value', value: inr(review.grandTotal) },
                { label: 'Order status', value: review.status },
                { label: 'Reason', value: review.cancellation?.reason },
                ...(review.cancellation?.note
                  ? [{ label: "Buyer's note", value: review.cancellation.note }]
                  : []),
              ]}
            />

            <Textarea
              label="Note (shown to the buyer)"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              placeholder="Why you approved or rejected this."
            />

            <div className="hint">
              Approving cancels the order, releases the reserved stock and notifies the seller.
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default Cancellations;
